/** @format */

import { useState } from "react";
import { Table, Button, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { IoNotifications, IoNotificationsOff } from "react-icons/io5";

import { LazyLoadImage } from "react-lazy-load-image-component";
import moment from "moment";
import DashboardBadge from "../DashboardBadge";
import {
  createWatchList,
  deleteWatchlist,
  modifyWatchlist,
} from "../../../redux/new-insight/actions";
import useServiceStatus from "../../../hooks/useServiceStatus";
import ServiceActivationModal from "../../../shared/service-activation/ServiceActivationModal";
import useFetchWatchlist from "../../../hooks/api/useFetchWatchlist";
import styles from "../../../styles/Components/new-dashboard/latest-signals/SignalsList.module.css";
import { getRelativeTime } from "../../../utility/formatter-helper";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

function SignalsTable() {
  const {
    dashboard: {
      signalInfo: { signals = [] },
    },
    newInsightState: {
      watchlists,
      watchlistStatus,
      createWatchlistStatus,
      modifyWatchListStatus,
      deleteWatchListStatus,
    },
    main: { userData },
  } = useSelector((state) => state);

  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);
  const [targetCoin, setTargetCoin] = useState(null);
  const [currentTpLevel, setCurrentTpLevel] = useState(1); // TP1 to TP5

  const dispatch = useDispatch();
  const { isActive } = useServiceStatus("insight");

  useFetchWatchlist();

  const sortedSignals = [...signals]?.sort(
    (a, b) => new Date(b?.signal_date) - new Date(a?.signal_date),
  );

  // Helper functions for TP/SL navigation
  const getTpValue = (signal, level) => {
    const tpKey = `tp${level}_percent`;
    return signal[tpKey] ? parseFloat(signal[tpKey]).toFixed(2) : "0.00";
  };

  const getSlValue = (signal) => {
    return signal.sl_percent
      ? parseFloat(signal.sl_percent).toFixed(2)
      : "0.00";
  };

  const handleTpNavigation = (direction) => {
    if (direction === "next" && currentTpLevel < 5) {
      setCurrentTpLevel(currentTpLevel + 1);
    } else if (direction === "prev" && currentTpLevel > 1) {
      setCurrentTpLevel(currentTpLevel - 1);
    }
  };

  const handleWatchlistActions = (signal) => {
    const { symbol } = signal;

    if (watchlistStatus === "loading") return;

    // Prevent user to create watchlist if insight service is not active
    if (!isActive) return setShowServiceActivationModal(true);

    setTargetCoin(symbol);

    const isCoinInList = watchlists?.[0]?.list_of_coins?.includes(symbol);

    if (watchlists.length === 0) {
      dispatch(
        createWatchList(
          userData["custom:custom_username"],
          userData?.email,
          [symbol],
          true,
        ),
      ).finally(() => setTargetCoin(null));
      return;
    }

    const currentWatchlist = watchlists?.[0];
    const createdDate = currentWatchlist?.createdDate;
    const coins =
      typeof currentWatchlist?.list_of_coins === "string"
        ? JSON.parse(currentWatchlist?.list_of_coins?.replace(/'/g, '"'))
        : currentWatchlist?.list_of_coins;

    if (coins.length === 1 && isCoinInList) {
      dispatch(
        deleteWatchlist(
          userData["custom:custom_username"],
          userData?.email,
          createdDate,
          true,
        ),
      ).finally(() => setTargetCoin(null));
      return;
    }

    const updatedCoinsList = isCoinInList
      ? coins.filter((c) => c !== symbol)
      : [...coins, symbol];
    dispatch(
      modifyWatchlist(
        userData["custom:custom_username"],
        userData?.email,
        createdDate,
        updatedCoinsList,
        true,
      ),
    ).finally(() => setTargetCoin(null));
  };

  const columns = [
    {
      title: "Symbol",
      dataIndex: "symbol",
      key: "symbol",
      width: 90,
      fixed: "left",
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      align: "center",
      render: (symbol) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            justifyContent: "center",
          }}
        >
          <LazyLoadImage
            src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${symbol?.replace("/USDT", "")}.png`}
            alt={symbol}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/placeholders/asset-logo.svg";
            }}
            width="24px"
            height="24px"
            effect="blur"
            style={{ borderRadius: "100%" }}
          />
          <span>
            {symbol}
            {symbol?.toLowerCase()?.includes("/usdt") ? "" : "/USDT"}
          </span>
        </div>
      ),
    },
    {
      title: "Trade Type",
      dataIndex: "trade_type",
      key: "trade_type",
      width: 70,
      fixed: "left",
      align: "center",
      render: (type) => (
        <div
          style={{
            display: "flex",
            gap: "2px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <DashboardBadge
            type={
              type === "Buy"
                ? "primary"
                : type === "Sell"
                  ? "dangerous"
                  : "primary"
            }
            usage="card"
            className={styles.badge}
            style={{ fontSize: "10px", padding: "2px 6px" }}
          >
            {type}
          </DashboardBadge>
        </div>
      ),
    },
    {
      title: "Entry Price",
      dataIndex: "entry_price",
      key: "entry_price",
      width: 90,
      responsive: ["lg", "xl", "xxl"],
      align: "center",
      render: (price) => `$${parseFloat(price).toFixed(2)}`,
    },
    {
      title: (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<LuChevronLeft />}
            onClick={() => handleTpNavigation("prev")}
            disabled={currentTpLevel === 1}
          />
          <span>TP{currentTpLevel} %</span>
          <Button
            type="text"
            size="small"
            icon={<LuChevronRight />}
            onClick={() => handleTpNavigation("next")}
            disabled={currentTpLevel === 5}
          />
        </Space>
      ),
      key: "tp_interactive",
      width: 100,
      // responsive: ['sm', 'md', 'lg', 'xl', 'xxl'],
      render: (_, signal) => `${getTpValue(signal, currentTpLevel)}%`,
      align: "center",
    },
    {
      title: "SL %",
      key: "sl_interactive",
      width: 50,
      // responsive: ['md', 'lg', 'xl', 'xxl'],
      render: (_, signal) => (
        <Space>
          <span>{getSlValue(signal)}%</span>
        </Space>
      ),
      align: "center",
    },
    {
      title: "Signal Date",
      dataIndex: "signal_date",
      key: "signal_date",
      width: 95,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      align: "center",
      render: (date) => {
        const signalDate = getRelativeTime(date);
        const now = moment();
        const signalTime = moment.utc(date).local();
        const diffInMinutes = now.diff(signalTime, "minutes");
        const isRecentSignal = diffInMinutes < 15;

        return (
          <span style={{ color: isRecentSignal ? "#52c41a" : "inherit" }}>
            {signalDate}
          </span>
        );
      },
    },
    {
      title: "Watchlist",
      key: "watchlist",
      width: 85,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      align: "center",
      render: (_, signal) => {
        const { symbol } = signal;
        const isCoinInList = watchlists?.[0]?.list_of_coins?.includes(symbol);

        return (
          <Button
            onClick={() => handleWatchlistActions(signal)}
            style={{ cursor: "pointer", fontSize: "10px", padding: "2px 6px" }}
            className={`${styles.btn} ${styles.watchListIcon}`}
            loading={
              targetCoin === symbol &&
              (createWatchlistStatus === "loading" ||
                modifyWatchListStatus === "loading" ||
                watchlistStatus === "loading" ||
                deleteWatchListStatus === "loading")
            }
            size="small"
          >
            {targetCoin === symbol &&
            (createWatchlistStatus === "loading" ||
              modifyWatchListStatus === "loading" ||
              watchlistStatus === "loading" ||
              deleteWatchListStatus === "loading") ? (
              ""
            ) : isCoinInList ? (
              <>
                <span style={{ fontSize: "10px" }}>Remove</span>
                <IoNotificationsOff size={12} />
              </>
            ) : (
              <>
                <span style={{ fontSize: "10px" }}>Add</span>
                <IoNotifications size={12} />
              </>
            )}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Table
        dataSource={sortedSignals}
        columns={columns}
        rowKey="signal_date"
        pagination={false}
        scroll={{ x: 600 }}
        size="small"
        className={styles.signalsTable}
      />
      {showServiceActivationModal && (
        <ServiceActivationModal
          service={"insai"}
          show={showServiceActivationModal}
          setShow={setShowServiceActivationModal}
        />
      )}
    </>
  );
}

export default SignalsTable;
